/**
 * What this is, where it comes from, and what it cannot tell you.
 *
 * The limits are on the page rather than in a footnote. A site that explains
 * laws with a language model and shows vote counts drawn from half a corpus
 * has to be legible about both, or the confident parts make the shaky parts
 * look confident too.
 */

import { Link } from "react-router-dom";
import { api } from "../api";
import { useAsync } from "../useAsync";

export function About() {
  const landscape = useAsync(() => api.landscape(), []);

  return (
    <div className="mx-auto max-w-2xl px-5 py-14">
      <h1 className="font-display text-4xl font-semibold">About</h1>

      <p className="mt-6 text-lg leading-relaxed text-pretty">
        Congress publishes everything it does. It publishes it as XML, in bulk archives, written
        for lawyers. This takes the same record and puts it in English.
      </p>

      <Section title="Where the facts come from">
        <p>
          Bill status, text and subject headings come from{" "}
          <a className="text-brand hover:underline" href="https://www.govinfo.gov/bulkdata">
            govinfo bulk data
          </a>
          , which is the Government Publishing Office's own feed. Roll calls come from the Clerk
          of the House and the Secretary of the Senate.
        </p>
        <p>
          Nothing is retyped and nothing is inferred. A vote count here is the count the chamber
          published, including the ones that do not add up the way you would expect.
        </p>
      </Section>

      <Section title="Where the English comes from">
        <p>
          The plain-English title and key points on each law are written by a language model,
          from that law's own filed text and nothing else. No outside knowledge is supplied to
          it, and every summary records what it was built from and how confident it was.
        </p>
        <p>
          It can still be wrong. It is a reading aid for a document that is public and one click
          away, not a replacement for it.
        </p>
      </Section>

      <Section title="What the votes can and cannot tell you">
        <p>
          {landscape.data ? (
            <>
              Of the {landscape.data.laws} laws held here, only{" "}
              <strong>{landscape.data.recorded}</strong> got a recorded vote.
              The other <strong>{landscape.data.voice}</strong> passed on a voice vote or by
              unanimous consent.
            </>
          ) : (
            <>Only about half of these laws got a recorded vote.</>
          )}{" "}
          For those there is no roll call and no way to say who was for it. That is shown as
          "nobody is on record" rather than as agreement, because a voice vote is a decision not
          to count, not a unanimous one.
        </p>
        <p>
          Where there is a roll call, only the vote on final passage is shown. A large bill can
          attract forty of them, and the split on a cloture motion or on the minority's motion to
          commit is usually the reverse of the split on passage.
        </p>
        <p>
          Absences are reported next to each party and never folded into the count. Treating an
          absence as opposition would turn a unanimous bill that half the chamber missed into a
          fifty-per-cent party.
        </p>
      </Section>

      <Section title="Members who have left">
        <p>
          Roll calls from 2021 are full of people who are no longer in Congress. 670 people have
          sat in the House since then against 435 seats. They are all still here, named, because
          somebody leaving office does not unmake how they voted.
        </p>
      </Section>

      <Section title="The API">
        <p>
          Everything on this site is available as JSON, and reading it needs no key. It is public
          record; the work was making it legible, not holding it back.
        </p>
        <p>
          The one exception is asking a question about a law, which puts the question to a language
          model and so costs a little money each time. That one is rationed rather than closed:
          anyone can ask a few an hour, and reading is never counted.
        </p>
        <p>
          <a className="font-medium text-brand hover:underline" href="/docs">
            Browse the API →
          </a>
        </p>
      </Section>

      <p className="mt-12 border-t border-line pt-6">
        <Link to="/laws" className="font-medium text-brand hover:underline">
          Start reading the laws →
        </Link>
      </p>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-10">
      <h2 className="font-display text-xl font-semibold">{title}</h2>
      <div className="mt-3 space-y-3 leading-relaxed text-muted">{children}</div>
    </section>
  );
}
